import React, { useState, useEffect, useRef, useCallback } from 'react';
import { TimePicker as MuiXTimePicker } from '@mui/x-date-pickers/TimePicker';
import { renderDigitalClockTimeView } from '@mui/x-date-pickers/timeViewRenderers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { da } from 'date-fns/locale';
import { InputAdornment, IconButton } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock } from '@fortawesome/pro-light-svg-icons';
import { formatTimeHHMM, formatDateTimePickerValue } from '../../utilities';
import { TextField } from '../text-field';
import { validateDefaultTimeFormat, validateHoursWithoutMinutes, validateHoursMinutesWithoutColon } from './time-validators';
import { isNullOrUndefined } from '../../utilities/null-undefined-helper';
import { useFeatureToggles } from '../../feature-toggles';
import { PICKER_TYPING_WITH_OPEN_POPPER_TOGGLE } from '../date-picker/picker-typing-toggle';
import { useReleaseFocusWhenPickerCloses } from '../date-picker/use-release-focus-when-picker-closes';

type TimePickerValue =
  | undefined
  | string
  | {
      hours: number;
      minutes: number;
    };

export interface MuiTimePickerProps {
  value: TimePickerValue;
  onChange: (timestamp: TimePickerValue) => void;
  name?: string;
  className?: string;
  label?: string;
  error?: boolean;
  errorText?: React.ReactNode;
  warningText?: string;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  inlineDisplay?: boolean;
  validators?: Array<(value: string) => TimePickerValue>;
  invalidDateMessage?: string;
  fullWidth?: boolean;
  variant?: 'filled' | 'outlined' | 'standard';
  minTime?: Date;
  maxTime?: Date;
  onInternalValidation?(failiureCause: string | undefined): void;
}

function validate(s: string, validators: Array<(value: string) => TimePickerValue>) {
  for (const validator of validators) {
    const result = validator(s);
    if (result) {
      return result;
    }
  }
  return undefined;
}

function timeValueToDate(value: TimePickerValue): Date | null {
  if (value === undefined || isNullOrUndefined(value) || typeof value === 'string') {
    return null;
  }

  if (Number.isNaN(value?.hours) || Number.isNaN(value?.minutes)) {
    return null;
  }

  if (Number.isNaN(value.hours) || Number.isNaN(value.minutes)) {
    return null;
  }

  const date = new Date();
  date.setHours(value.hours, value.minutes, 0, 0);

  return date;
}

function DEFAULT_TIME() {
  const d = new Date();
  d.setHours(8, 0, 0, 0);
  return d;
}

export function MuiTimePicker(props: MuiTimePickerProps) {
  const {
    value,
    onChange,
    name,
    className,
    label,
    required,
    readOnly,
    error,
    errorText,
    disabled,
    inlineDisplay,
    warningText,
    validators = [validateHoursWithoutMinutes, validateDefaultTimeFormat, validateHoursMinutesWithoutColon],
    invalidDateMessage = 'Indtast et gyldigt tidspunkt',
    variant = 'filled',
    fullWidth,
    minTime,
    maxTime,
    onInternalValidation,
  } = props;

  const [rawState, setRawState] = useState(formatTimeHHMM(value));
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const popperRef = useRef<HTMLDivElement>(null);

  const { isFeatureEnabled } = useFeatureToggles();
  const keepOpenOnFieldClick = isFeatureEnabled(PICKER_TYPING_WITH_OPEN_POPPER_TOGGLE);
  const { handleFieldFocus, keepFocusAfterPick } = useReleaseFocusWhenPickerCloses(isOpen, keepOpenOnFieldClick, popperRef);

  useEffect(() => {
    setRawState(formatTimeHHMM(value));
  }, [value]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const id = window.setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
    return () => {
      window.clearTimeout(id);
    };
  }, [isOpen]);

  const apply = useCallback(() => {
    if (!rawState) {
      onChange(undefined);
      return;
    }

    const validatedTime = validate(rawState, validators);
    if (validatedTime) {
      onChange(validatedTime);
      setRawState(formatTimeHHMM(validatedTime));
    }
    onInternalValidation?.(validatedTime === undefined ? invalidDateMessage : undefined);
  }, [rawState, validators, onChange, onInternalValidation, invalidDateMessage]);

  const onChangeCalledRef = useRef(false);

  function handlePickerChange(date: Date | null) {
    keepFocusAfterPick();
    onChangeCalledRef.current = true;
    if (date == null) {
      onChange(undefined);
      setRawState(undefined);
      return;
    }
    const timeValue = formatDateTimePickerValue(date);
    onChange(timeValue);
    setRawState(formatTimeHHMM(timeValue));
    onInternalValidation?.(undefined);
  }

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    let val = event.target.value.replace(/[^\d:]/g, '');
    if (!val.includes(':') && val.length >= 3) {
      val = `${val.slice(0, 2)}:${val.slice(2)}`;
    }
    setRawState(val);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter' || event.key === 'Tab') {
      apply();
      setIsOpen(false);
    }
  }

  function handleFieldClick(event: React.MouseEvent) {
    if (disabled || readOnly) {
      return;
    }
    if (keepOpenOnFieldClick && isOpen) {
      // Keeps the picker's click-away listener on document from closing it while the user types.
      event.stopPropagation();
      return;
    }
    setIsOpen(true);
  }

  function handleBlur(event: React.FocusEvent<HTMLInputElement>) {
    if (popperRef.current?.contains(event.relatedTarget as Node | null)) {
      return;
    }
    apply();
  }

  const timeError = rawState && validate(rawState, validators) === undefined ? invalidDateMessage : undefined;

  const wrapperClasses = [inlineDisplay ? 'inline-display' : '', !fullWidth ? 'time-picker' : ''].filter(Boolean).join(' ');

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={da}>
      <div className={wrapperClasses || undefined} ref={triggerRef}>
        <TextField
          inputRef={inputRef}
          className={className}
          label={label}
          value={rawState}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onClick={handleFieldClick}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="large"
                  tabIndex={-1}
                  onClick={e => {
                    e.stopPropagation();
                    if (!disabled && !readOnly) {
                      setIsOpen(prev => !prev);
                    }
                  }}
                >
                  <FontAwesomeIcon icon={faClock} size="xs" />
                </IconButton>
              </InputAdornment>
            ),
            readOnly,
          }}
          error={error || !!timeError}
          errorText={(error || !!timeError) && (errorText || (timeError ? String(timeError) : undefined))}
          helperText={warningText}
          disabled={disabled}
          required={required}
          readOnly={readOnly}
          fullWidth={fullWidth}
          variant={variant}
          multiline={false}
          inputProps={{
            autoComplete: 'off',
            placeholder: 'HH:mm',
            name,
            onFocus: handleFieldFocus,
          }}
        />
        <MuiXTimePicker
          open={isOpen}
          onOpen={() => setIsOpen(true)}
          onClose={() => setIsOpen(false)}
          value={timeValueToDate(value) ?? DEFAULT_TIME()}
          onChange={handlePickerChange}
          ampm={false}
          views={['hours']}
          viewRenderers={{
            hours: renderDigitalClockTimeView,
          }}
          timeSteps={{ minutes: 30 }}
          minTime={minTime}
          maxTime={maxTime}
          disabled={disabled}
          readOnly={readOnly}
          slots={{ textField: () => null }}
          slotProps={{
            desktopTrapFocus: {
              disableEnforceFocus: true,
            },
            popper: {
              ref: popperRef,
              anchorEl: triggerRef.current,
              placement: 'bottom-start',
              onClick: () => {
                if (!timeValueToDate(value) && !onChangeCalledRef.current) {
                  handlePickerChange(DEFAULT_TIME());
                }
                onChangeCalledRef.current = false;
              },
            },
            digitalClockItem: {
              sx: { textAlign: 'center' },
            },
            actionBar: {
              actions: [],
            },
            layout: {
              sx: {
                '.MuiDigitalClock-root': {
                  overflowY: 'scroll',
                },
              },
            },
          }}
        />
      </div>
    </LocalizationProvider>
  );
}
